import {
  type ForwardedRef,
  forwardRef,
  type HTMLAttributes,
  type ReactElement,
} from 'react';

import { cn } from '@/shared/lib/cn';

type DivProps = HTMLAttributes<HTMLDivElement>;
type HeadingProps = HTMLAttributes<HTMLHeadingElement>;
type ParagraphProps = HTMLAttributes<HTMLParagraphElement>;

export const Card = forwardRef(function Card(
  { className, ...props }: DivProps,
  ref: ForwardedRef<HTMLDivElement>,
): ReactElement {
  return (
    <div
      ref={ref}
      className={cn(
        'rounded-xl border border-border bg-card text-card-foreground shadow-soft-1',
        className,
      )}
      {...props}
    />
  );
});

export const CardHeader = forwardRef(function CardHeader(
  { className, ...props }: DivProps,
  ref: ForwardedRef<HTMLDivElement>,
): ReactElement {
  return <div ref={ref} className={cn('flex flex-col gap-1.5 p-6', className)} {...props} />;
});

export const CardTitle = forwardRef(function CardTitle(
  { className, children, ...props }: HeadingProps,
  ref: ForwardedRef<HTMLHeadingElement>,
): ReactElement {
  return (
    <h3
      ref={ref}
      className={cn(
        'font-display text-lg font-semibold leading-none tracking-tight',
        className,
      )}
      {...props}
    >
      {children}
    </h3>
  );
});

export const CardDescription = forwardRef(function CardDescription(
  { className, ...props }: ParagraphProps,
  ref: ForwardedRef<HTMLParagraphElement>,
): ReactElement {
  return (
    <p ref={ref} className={cn('text-sm text-muted-foreground', className)} {...props} />
  );
});

export const CardContent = forwardRef(function CardContent(
  { className, ...props }: DivProps,
  ref: ForwardedRef<HTMLDivElement>,
): ReactElement {
  return <div ref={ref} className={cn('p-6 pt-0', className)} {...props} />;
});

export const CardFooter = forwardRef(function CardFooter(
  { className, ...props }: DivProps,
  ref: ForwardedRef<HTMLDivElement>,
): ReactElement {
  return (
    <div
      ref={ref}
      className={cn('flex items-center gap-2 p-6 pt-0', className)}
      {...props}
    />
  );
});
